import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { USER_ROLES } from '@pwc/pwc-deals-web-common';

import { sortProjectsByPurposeAndID } from 'common/helpers/projectInitUtil';
import {
  getProjects,
  addProjectToFavourite,
  deleteProjectfromFavourite,
  getListCount,
  getFavouriteList,
  getSearchProjectCount,
  getSearchProject,
} from '../../common/services/projectService';

import userTokenService from '../../common/services/security/userTokenService';

export const pagedSearchProjectsInitialState = {
  loading: false,
  error: null,
  projects: [],
  total: 0,
  pageSize: 10,
  currentPage: 1,
  totalCount: 0,
};

export const initialState = {
  loading: true,
  error: null,
  projects: [],
  filter: '',
  total: 0,
  pageSize: 10,
  currentPage: 0,
  hasItems: false,
  initiallyLoaded: false,
  allProjects: { total: 0, projects: [], currentPage: 0, loaded: false },
  favourites: {
    loading: false,
    error: null,
    projects: [],
    totalCount: 0,
    favouriteInProgress: {
      projectId: '',
    },
  },
  allProjectsCount: {
    loading: false,
    error: null,
    totalCount: 0,
  },
  pagedSearchProjects: {
    loading: false,
    error: null,
    projects: [],
    total: 0,
    pageSize: 10,
    currentPage: 1,
    totalCount: 0,
  },
};

export const getPagedProjects = createAsyncThunk(
  'pagedProjects/getPagedProjects',
  async ({ page, pageSize, filter = 1 }, thunk) => {
    const state = thunk.getState();
    const isPwcUser = state.loggedInUser.userRole === USER_ROLES.PWC_USER;

    try {
      const response = await getProjects(
        page,
        pageSize,
        filter,
        isPwcUser ? 'purpose desc, id' : undefined
      );
      return { response, filter };
    } catch (error) {
      return {
        response: { items: [], currentPage: 1, pageSize: 0, pageCount: 0 },
        filter,
      };
    }
  }
);

export const getSearchPagedProjects = createAsyncThunk(
  'pagedSearchProjects/getSearchPagedProjects',
  async ({ page, pageSize, filter = 1, searchString }, { rejectWithValue }) => {
    try {
      const isInternalUser = userTokenService.isUserInternal();
      const response = await getSearchProject(
        page,
        pageSize,
        searchString,
        filter,
        isInternalUser ? 'purpose desc, id' : undefined,
        'true',
        isInternalUser
      );
      return { response, filter };
    } catch (error) {
      return rejectWithValue({
        response: { items: [], currentPage: 1, pageSize: 10 },
      });
    }
  }
);

export const getCountPagedProjects = createAsyncThunk(
  'pagedSearchProjects/getSearchProjectCount',
  async ({ page, pageSize, filter = 1, searchString }, { rejectWithValue }) => {
    try {
      const isInternalUser = userTokenService.isUserInternal();
      const response = await getSearchProjectCount(
        page,
        pageSize,
        searchString,
        filter,
        isInternalUser ? 'purpose desc, id' : undefined,
        'true',
        isInternalUser
      );
      return { response, filter };
    } catch (error) {
      return rejectWithValue({
        response: { count: 0 },
      });
    }
  }
);

export const getFavouriteProjectList = createAsyncThunk(
  'pagedProjects/getFavouriteProjectList',
  async ({ filter }, { rejectWithValue }) => {
    try {
      const isInternalUser = userTokenService.isUserInternal();
      const response = await getFavouriteList(
        filter,
        isInternalUser ? 'purpose desc, id' : undefined,
        'true'
      );
      return { response };
    } catch (error) {
      return rejectWithValue({
        response: { items: [], currentPage: 1, pageSize: 10 },
      });
    }
  }
);

export const getProjectListCount = createAsyncThunk(
  'pagedProjects/getProjectListCount',
  async ({ filter }, { rejectWithValue }) => {
    try {
      const response = await getListCount(filter);
      return { response };
    } catch (error) {
      return rejectWithValue({
        response: { count: 0 },
      });
    }
  }
);

export const addToFavourite = createAsyncThunk(
  'pagedProjects/setProjectToFavourite',
  async ({ projectId }, { rejectWithValue, dispatch }) => {
    try {
      // eslint-disable-next-line no-use-before-define
      dispatch(addProjectToFavInProgress({ projectId }));
      const response = await addProjectToFavourite(projectId);
      return { response, projectId };
    } catch (error) {
      return rejectWithValue({
        response: false,
      });
    }
  }
);

export const deleteFromFavourite = createAsyncThunk(
  'pagedProjects/deleteFromFavourite',
  async ({ projectId }, { rejectWithValue, dispatch }) => {
    try {
      // eslint-disable-next-line no-use-before-define
      dispatch(addProjectToFavInProgress({ projectId }));
      const response = await deleteProjectfromFavourite(projectId);
      return { response, projectId };
    } catch (error) {
      return rejectWithValue({
        response: false,
      });
    }
  }
);

const pagedProjectsSlice = createSlice({
  name: 'pagedProjects',
  initialState,
  reducers: {
    clearFilter: (state) => {
      const { allProjects } = state;
      const { projects, total, currentPage } = allProjects;
      state.total = total;
      state.currentPage = currentPage;
      state.projects = projects;
      state.allProjects = {};
    },
    clearText: (state) => {
      state.pagedSearchProjects = pagedSearchProjectsInitialState;
    },
    addProjectToFavInProgress: (state, { payload }) => {
      const { projectId } = payload;
      state.favourites.favouriteInProgress.projectId = projectId;
    },
  },
  extraReducers: {
    [getPagedProjects.pending]: (state) => {
      state.loading = true;
      state.error = null;
    },
    [getPagedProjects.fulfilled]: (state, { payload }) => {
      const { response, filter } = payload;
      const { items, currentPage, pageSize, pageCount } = response;
      state.loading = false;
      state.error = null;
      if (!state.initiallyLoaded) {
        state.initiallyLoaded = true;
        state.hasItems = items.length > 0;
      }
      // Save previous ALL items
      if (filter !== 1 && !state.allProjects.loaded) {
        state.allProjects = {
          projects: state.projects,
          total: state.total,
          currentPage: state.currentPage,
          loaded: true,
        };
      }
      if (currentPage === 1) {
        state.projects = [...items];
      } else {
        state.projects = [...state.projects, ...items];
      }
      state.total = pageSize * pageCount;
      state.currentPage = currentPage;
    },
    [getPagedProjects.rejected]: (state, { error }) => {
      state.loading = false;
      state.error = error;
      state.projects = [];
    },
    [getSearchPagedProjects.pending]: (state) => {
      state.pagedSearchProjects.loading = true;
      state.pagedSearchProjects.error = null;
    },
    [getSearchPagedProjects.fulfilled]: (state, { payload }) => {
      const { response } = payload;
      const { items, currentPage, pageSize, pageCount } = response;
      state.pagedSearchProjects.loading = false;
      state.pagedSearchProjects.error = null;
      if (currentPage === 1) {
        state.pagedSearchProjects.projects = [...items];
      } else {
        state.pagedSearchProjects.projects = [
          ...state.pagedSearchProjects.projects,
          ...items,
        ];
      }
      state.pagedSearchProjects.total = pageSize * pageCount;
      state.pagedSearchProjects.currentPage = currentPage;
    },
    [getSearchPagedProjects.rejected]: (state, { error, payload }) => {
      const { response } = payload;
      const { pageSize, currentPage, items } = response;
      state.pagedSearchProjects.pageSize = pageSize;
      state.pagedSearchProjects.currentPage = currentPage;
      state.pagedSearchProjects.projects = items;
      state.pagedSearchProjects.loading = false;
      state.pagedSearchProjects.total = 0;
      state.pagedSearchProjects.error = error.message;
    },
    [getCountPagedProjects.pending]: (state) => {
      state.pagedSearchProjects.countLoading = true;
      state.pagedSearchProjects.countError = null;
    },
    [getCountPagedProjects.fulfilled]: (state, { payload }) => {
      const { response } = payload;
      state.pagedSearchProjects.totalCount = response;
      state.pagedSearchProjects.countLoading = false;
      state.pagedSearchProjects.countError = null;
    },
    [getCountPagedProjects.rejected]: (state, { error }) => {
      state.pagedSearchProjects.countLoading = false;
      state.pagedSearchProjects.countError = error.message;
      state.pagedSearchProjects.totalCount = 0;
    },
    [getFavouriteProjectList.pending]: (state) => {
      state.favourites.loading = true;
      state.favourites.error = null;
    },
    [getFavouriteProjectList.fulfilled]: (state, { payload }) => {
      const { response } = payload;
      const { items } = response;
      state.favourites.loading = false;
      state.favourites.error = null;
      state.favourites.projects = items;
      state.favourites.totalCount = items.length;
    },
    [getFavouriteProjectList.rejected]: (state, { error }) => {
      state.favourites.loading = false;
      state.favourites.error = error;
      state.favourites.totalCount = 0;
      state.favourites.projects = [];
    },
    [getProjectListCount.pending]: (state) => {
      state.allProjectsCount.loading = true;
      state.allProjectsCount.error = null;
    },
    [getProjectListCount.fulfilled]: (state, { payload }) => {
      const { response } = payload;
      state.allProjectsCount.loading = false;
      state.allProjectsCount.error = null;
      state.allProjectsCount.totalCount = response;
    },
    [getProjectListCount.rejected]: (state, { error }) => {
      state.allProjectsCount.loading = false;
      state.allProjectsCount.error = error;
      state.allProjectsCount.totalCount = 0;
    },
    [deleteFromFavourite.fulfilled]: (state, { payload }) => {
      const { projectId } = payload;
      const index = state.projects.findIndex(
        (project) => project.id === projectId
      );
      const favIndex = state.favourites.projects.findIndex(
        (project) => project.id === projectId
      );
      const searchIndex = state.pagedSearchProjects.projects.findIndex(
        (project) => project.id === projectId
      );
      if (favIndex >= 0) {
        state.favourites.projects.splice(favIndex, 1);
        state.favourites.totalCount -= 1;
        if (state.favourites.totalCount < 0) {
          state.favourites.totalCount = 0;
        }
      }
      if (index >= 0) {
        state.projects[index].isUserFavorite = false;
      }
      if (searchIndex >= 0) {
        state.pagedSearchProjects.projects[searchIndex].isUserFavorite = false;
      }
      state.favourites.favouriteInProgress.projectId = '';
    },
    [deleteFromFavourite.rejected]: (state) => {
      state.favourites.favouriteInProgress.projectId = '';
    },
    [addToFavourite.fulfilled]: (state, { payload }) => {
      const { projectId, response } = payload;
      const index = state.projects.findIndex(
        (project) => project.id === projectId
      );
      const searchProjectIndex = state.pagedSearchProjects.projects.findIndex(
        (project) => project.id === projectId
      );
      const favIndex = state.favourites.projects.findIndex(
        (project) => project.id === projectId
      );
      // when user clicks on fav icon in search page --> update the search list, all projects list and push that porject to fav list
      if (searchProjectIndex >= 0) {
        state.pagedSearchProjects.projects[searchProjectIndex].isUserFavorite =
          response;
        if (index >= 0) {
          state.projects[index].isUserFavorite = response;
        }
        if (favIndex === -1) {
          state.favourites.projects.push(
            state.pagedSearchProjects.projects[searchProjectIndex]
          );
          sortProjectsByPurposeAndID(state.favourites.projects);
          state.favourites.totalCount += 1;
        }
      } // when user clicks on fav icon from all projects --> update all projects list and push that project to fav list
      else if (index >= 0) {
        state.projects[index].isUserFavorite = response;
        if (favIndex === -1) {
          state.favourites.projects.push(state.projects[index]);
          sortProjectsByPurposeAndID(state.favourites.projects);
          state.favourites.totalCount += 1;
        }
      }
      state.favourites.favouriteInProgress.projectId = '';
    },
    [addToFavourite.rejected]: (state) => {
      state.favourites.favouriteInProgress.projectId = '';
    },
  },
});

export const { clearFilter, clearText, addProjectToFavInProgress } =
  pagedProjectsSlice.actions;
export const { reducer: pagedProjectsReducer } = pagedProjectsSlice;

export const getPagedProjectsSelector = (state) => state.pagedProjects;

export const getAllProjects = (state) => state.pagedProjects.projects;

export const getFavProjects = (state) =>
  state.pagedProjects.favourites.projects;

export const getFavProjectsCount = (state) =>
  state.pagedProjects.favourites.totalCount;

export const getAllProjectCount = (state) =>
  state.pagedProjects.allProjectsCount.totalCount;

export const isFavLoading = (state) => state.pagedProjects.favourites.loading;

export const getSearchPagedProjectsSelector = (state) =>
  state.pagedProjects.pagedSearchProjects.projects;

export const getSearchLoading = (state) =>
  state.pagedProjects.pagedSearchProjects.loading;

export const getSearchTotal = (state) =>
  state.pagedProjects.pagedSearchProjects.total;

export const getSearchCurrentPage = (state) =>
  state.pagedProjects.pagedSearchProjects.currentPage;

export const getSearchCount = (state) =>
  state.pagedProjects.pagedSearchProjects.totalCount;

export const getProjectFavInProgress = (state) =>
  state.pagedProjects.favourites.favouriteInProgress.projectId;
